import {
  Button,
  Input,
  Modal,
  TablePro,
  Tag,
  Tooltip,
} from "@gw/web-basic-components";
import React, { useEffect, useState } from "react";
import ValueEditor from "../../value-editor";
import { Field, Formula, TableEditorConfig } from "../../dto";
import { produce } from "immer";
import DotLabel from "../../../dot-label";
import useFlattenFields from "../../hooks/use-flatten-fields";
import { getListBindsByValue, isFormulaHasListField } from "../../utils";

import { ReactComponent as TableEditIcon } from "../../assets/table-edit.svg";
import { ReactComponent as TableResetIcon } from "../../assets/table-reset.svg";
import { ReactComponent as TableDeleteIcon } from "../../assets/table-delete.svg";

const getUniqueStr = (name: string, names: string[]) => {
  let uniqueName = name;
  let i = 1;
  while (names.includes(uniqueName)) {
    uniqueName = `${name}_${i}`;
    i++;
  }
  return uniqueName;
};

const unique = (arr: string[]) => Array.from(new Set(arr));

export interface FormulaSettingProps {
  open?: boolean;
  fields: Field[];
  onOpenChange?: (open: boolean) => void;
  value?: TableEditorConfig["formulas"];
  onChange?: (value: TableEditorConfig["formulas"]) => void;
}

const FormulaSetting = ({
  open,
  fields,
  onOpenChange,
  value,
  onChange,
}: FormulaSettingProps) => {
  const [dataSource, setDataSource] = useState<TableEditorConfig["formulas"]>(
    []
  );

  const [current, setCurrent] = useState<TableEditorConfig["formulas"][0]>();

  const [edit, setEdit] = useState(false);

  const [flds, fldsCodes, fldsNames] = useFlattenFields(fields);

  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDataSource(value ?? []);
  }, [open]);

  return (
    <Modal
      width="56.25rem"
      open={open}
      title="自定义参数"
      onOk={() => {
        onChange?.(dataSource);
        onOpenChange?.(false);
      }}
      onCancel={() => onOpenChange?.(false)}
      okText="确定"
      cancelText="取消"
    >
      <ValueEditor
        required
        type="formula"
        config={fields}
        disabled={(cur, item) => {
          if (!item.isList) return false;
          const { parentCodes } = getListBindsByValue(cur, flds);
          if (parentCodes.length === 0) return false;
          return !parentCodes.includes(item.parentCode);
        }}
        trigger={(onClick) => (
          <div className="wbuc-p-[1.875rem]">
            <div className="wbuc-flex wbuc-items-center wbuc-justify-between wbuc-border-b wbuc-border-l-0 wbuc-border-r-0 wbuc-border-t-0 wbuc-border-solid wbuc-border-[rgba(1,1,1,0.1)] wbuc-pb-4">
              <DotLabel>参数编辑</DotLabel>
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  setEdit(false);
                  onClick();
                  setCurrent(undefined);
                  setError(false);
                }}
              >
                添加自定义
              </Button>
            </div>
            <TablePro
              dataSource={dataSource}
              rowKey="name"
              columns={[
                {
                  title: "参数",
                  dataIndex: "name",
                  key: "name",
                  render: (text, record) => (
                    <div className="wbuc-flex wbuc-items-center wbuc-gap-2">
                      <span>{text}</span>
                      {record.custom ? (
                        <Tag color="#3B65EE">自定义</Tag>
                      ) : (
                        <Tag color="#595962">系统项</Tag>
                      )}
                    </div>
                  ),
                },
                {
                  title: "逻辑",
                  dataIndex: "value",
                  key: "value",
                  ellipsis: true,
                  render: (text) =>
                    text
                      ?.map((i) =>
                        i.type === "dynamic" ? `\${${i.label}}` : i.label
                      )
                      .join(" "),
                },
                {
                  title: "操作",
                  key: "action",
                  width: "6rem",
                  render: (record) => (
                    <div className="wbuc-flex wbuc-items-center wbuc-gap-2">
                      <Tooltip title="编辑">
                        <TableEditIcon
                          className="wbuc-w-5 wbuc-cursor-pointer"
                          onClick={() => {
                            setEdit(true);
                            onClick();
                            setCurrent(record);
                            setError(false);
                          }}
                        />
                      </Tooltip>
                      {record.custom ? (
                        <Tooltip title="删除">
                          <TableDeleteIcon
                            className="wbuc-w-5 wbuc-cursor-pointer"
                            onClick={() => {
                              const { code } = record;
                              setDataSource((d) =>
                                produce(d, (draft) => {
                                  const idx = draft.findIndex(
                                    (i) => i.code === code
                                  );
                                  draft.splice(idx, 1);
                                })
                              );
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="重置">
                          <TableResetIcon
                            className="wbuc-w-5 wbuc-cursor-pointer"
                            onClick={() => {
                              const { code } = record;
                              setDataSource((d) =>
                                produce(d, (draft) => {
                                  const idx = draft.findIndex(
                                    (i) => i.code === code
                                  );
                                  const defaultValue =
                                    flds.find((i) => i.code === code)
                                      ?.formula ?? [];
                                  draft[idx].value = defaultValue;
                                })
                              );
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
        value={current?.value}
        onChange={(v) => {
          if (!current?.name) {
            setError(true);
            // 阻止自定义值编辑弹窗关闭
            throw new Error("请先输入参数名称");
          }
          setDataSource((d) =>
            produce(d, (draft) => {
              if (edit) {
                const idx = draft.findIndex((i) => i.code === current?.code);
                draft[idx].value = v as Formula["value"];
                if (draft[idx].name !== current?.name) {
                  draft[idx].name = getUniqueStr(
                    current?.name,
                    unique([
                      ...draft
                        .map((i) => i.name)
                        .filter((i) => i !== draft[idx].name),
                      ...fldsNames,
                    ])
                  );
                }
              } else {
                draft.unshift({
                  code: getUniqueStr(
                    v
                      .filter((i) => i.type !== "operator")
                      .map((i) => i.value)
                      .join("_"),
                    unique([...draft.map((i) => i.code), ...fldsCodes])
                  ),
                  name: getUniqueStr(
                    current?.name,
                    unique([...draft.map((i) => i.name), ...fldsNames])
                  ),
                  value: v as Formula["value"],
                  custom: true,
                  isList: isFormulaHasListField(v as Formula["value"], flds),
                });
              }
            })
          );
        }}
      >
        <div className="wbuc-flex wbuc-items-center wbuc-gap-2">
          <span className="wbuc-flex-shrink-0">参数名称：</span>
          {current?.custom || !edit ? (
            <Input
              size="middle"
              value={current?.name}
              onChange={(e) => {
                const v = e.target.value;
                setCurrent((c) => ({ ...c, name: v }));
                setError(!v);
              }}
              placeholder="请输入参数名称"
              status={error ? "error" : undefined}
            />
          ) : (
            <span>{current?.name}</span>
          )}
        </div>
      </ValueEditor>
    </Modal>
  );
};

export default FormulaSetting;
